import { Injectable, HostListener } from '@angular/core';
import { PusherService } from 'ng2-app/modules/shared/services/pusher.service';
import { Observable, merge } from 'rxjs';
import { filter, map, tap, switchMap, finalize } from 'rxjs/operators';
import { ApiHttpClient } from 'app/http/clients/api';

export interface StateEvent {
    path: string;
    data: any;
}

export interface StateChangedEvent {
    path: string;
}

@Injectable()
export class StateService {

    private _stateEvents: Observable<StateEvent>;
    private _stateChangedEvents: Observable<StateChangedEvent>;
    private subscribedPaths: string[] = [];
    private clientId: string;
    private get subscribed(): boolean {
      return this.subscribedPaths.length > 0;
    };

    constructor(
        private pusherService: PusherService,
        private http: ApiHttpClient
    ) {
        this.clientId = this.generateClientId();

        this.pusherService.subscribe(`private-client-${this.clientId}-channel`);

        this._stateEvents = this.pusherService.events.pipe(
            filter((e) => e.hasOwnProperty('name') && e.name === 'state'),
            map(e => e.data),
            map(e => {
                if (typeof e.data === 'string') {
                    Object.assign(e, { data: JSON.parse(e.data) });
                }
                return e;
            })
        );

        this._stateChangedEvents = this.pusherService.events.pipe(
            filter((e) => e.hasOwnProperty('name') && e.name === 'stateChanged'),
            map((e) => e.data)
        );

        (window as any).addEventListener('beforeunload', () => {
            this.unsubscribeAll();
        });
    }

    /**
     * Emits the state for the given path as it changes over time. This is accomplished by requesting the state
     * initially via an AJAX request and then subscribing to subsequent state changes which are pushed via Pusher.
     *
     * There are two flavors of state change. The first, which comes from 'state' events, includes the data in the
     * payload of the push event. The second, from 'stateChanged' events, does not include the data in the payload,
     * so we immediately make an AJAX request for it. This handles state changes which exceed the maximum Pusher
     * payload size.
     *
     * @param path A path string representing the state to which you want to subscribe. Should be the same for the
     * path included in pushed state events and the URL used to make AJAX requests for the state.
     */
    select<T>(path: string): Observable<T> {
        const initialRequest$ = this.http.get<T>('/' + path).pipe(
            tap(() => this.subscribe(path))
        );

        const state$ = this._stateEvents.pipe(
            filter(e => e.path === path),
            map(e => e.data as T)
        );

        const stateChanged$ = this._stateChangedEvents.pipe(
            filter(e => e.path === path),
            switchMap(e => this.http.get<T>('/' + e.path))
        );

        return merge(initialRequest$, state$, stateChanged$).pipe(
            finalize(() => this.unsubscribe(path))
        );
    }

    /**
     * Just like select, but without the GET request. Aka only the stream of future messages.
     *
     * @param path
     */
    stream<T>(path: string): Observable<T> {
        this.subscribe(path);
        return this._stateEvents.pipe(
            filter(e => e.path === path),
            map(e => e.data as T),
            finalize(() => this.unsubscribe(path))
        );
    }

    @HostListener('window:beforeunload')
    unsubscribeAll() {
        if (this.subscribed && this.clientId) {
          this.http.post('/state-subscription/unsubscribe', {paths: this.subscribedPaths, clientId: this.clientId}).subscribe();
        }
    }

    private subscribe(path: string) {
        return this.http.post('/state-subscription', {paths: [path], clientId: this.clientId }).subscribe(() => {
            this.subscribedPaths.push(path);
        });
    }

    private unsubscribe(path: string) {
        this.http.post('/state-subscription/unsubscribe', {paths: [path], clientId: this.clientId }).subscribe({
          next: () => {
            if (this.subscribedPaths.indexOf(path) >= 0) {
                this.subscribedPaths.splice(this.subscribedPaths.indexOf(path), 1);
            }
          },
          error: (err) => {
            let errorAsString: string;
            if (typeof err === 'string') {
              errorAsString = err;
            } else if (err?.toString) {
              errorAsString = err.toString();
            } else {
              errorAsString = 'state.service.ts > unsubscribe';
            }
            throw new Error(errorAsString);
          }
        });
    }

    private generateClientId(): string {

        // This is not close to cryptographic quality, but it's good enough for what we need
        return Math.floor((Math.random() * 1000000000000)).toString(16);
    }
}
